// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
'use client';
// Flameco AI (Ask Alpha) in a Flameco app, and the hand-off from "Search everything"
// to Flameco AI. Alpha hosts the widget (alpha-chat.js) and its API, checks the
// person's Flameco token and alpha.* tags itself, and keeps the widget hidden
// for people without access, so search only offers Flameco AI when it is visible.
//
// Apps that already mount their own <alpha-chat> (World, Analytics, Legacy ERP)
// need nothing here: search finds that element. Other apps pass `doug` to
// FlamecoFrame, which mounts <DougWidget>.
import React, { useEffect, useRef } from 'react';

export interface DougConfig {
  /** Alpha's API, e.g. https://agentbackend.loclx.io. */
  api: string;
  /** The app's slug registered in Alpha (also its token audience there). */
  app: string;
  /** Shown in Flameco AI's greeting. */
  appName: string;
  /** Where alpha-chat.js lives; default https://alpha.flameco.com/widget/. */
  widgetBase?: string;
  /** The app's current Flameco access token (flameco-auth getToken). */
  getToken: () => string | null;
  /** Starter questions before the app's guide supplies its own. */
  suggestions?: string[];
  /** Current route path, so Flameco AI knows the page you're on. */
  currentPath?: string;
  /** In-app navigation for Flameco AI's links to this app's pages (router push). */
  navigate?: (path: string) => void;
}

type AlphaChatEl = HTMLElement & {
  getToken: () => string | null;
  context: Record<string, unknown> | null;
  suggestions: string[];
  open: () => void;
  ask: (text: string) => Promise<void>;
};

const DEFAULT_WIDGET_BASE = 'https://alpha.flameco.com/widget/';

/** The page's Flameco AI, once Alpha has said this person may use it. */
function dougElement(): AlphaChatEl | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('alpha-chat') as AlphaChatEl | null;
  if (!el || el.hidden || typeof el.ask !== 'function') return null;
  return el;
}

/** Can this person ask Flameco AI on this page right now? */
export function dougAvailable(): boolean {
  return !!dougElement();
}

/** Where and as whom to talk to Alpha, read from the page's Flameco AI (so the same access rules apply). */
export function dougConnection(): {
  api: string;
  app: string;
  getToken: () => string | null;
  context: Record<string, unknown>;
} | null {
  const el = dougElement();
  const api = el?.getAttribute('api');
  const app = el?.getAttribute('app');
  if (!el || !api || !app || typeof el.getToken !== 'function') return null;
  const ctx = (el.context ?? {}) as Record<string, unknown>;
  const page = ctx.page ?? { path: window.location.pathname, title: document.title.slice(0, 200) };
  return { api, app, getToken: () => el.getToken(), context: { ...ctx, page } };
}

/**
 * Opens the Flameco AI chat on a conversation that started in search: same thread on
 * Alpha's side, and the question and answer shown so the chat makes sense.
 */
export function openDougThread(threadId: string | null, shown: { question: string; answerHtml: string }[] = []) {
  const el = dougElement() as (AlphaChatEl & { threadId?: string | null; _say?: (who: string, html: string) => unknown }) | null;
  if (!el) return;
  if (threadId) el.threadId = threadId;
  if (typeof el._say === 'function') {
    const esc = (t: string) => t.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
    for (const turn of shown) {
      el._say('me', esc(turn.question));
      el._say('bot', turn.answerHtml);
    }
  }
  el.style.visibility = '';
  el.open();
}

/** Closes Flameco AI's chat panel (the conversation stays). */
export function minimizeDoug() {
  const el = dougElement() as (AlphaChatEl & { close?: () => void }) | null;
  if (el?.classList.contains('is-open')) el.close?.();
}

/** Hides Flameco AI's button and teaser (e.g. under the search panel), or shows them again. */
export function hideDoug(hidden: boolean) {
  const el = typeof document === 'undefined' ? null : (document.querySelector('alpha-chat') as HTMLElement | null);
  if (el) el.style.visibility = hidden ? 'hidden' : '';
}

/** Opens Flameco AI and asks `text`, as if typed into the chat. */
export function askDoug(text: string): boolean {
  const el = dougElement();
  if (!el) return false;
  el.open();
  void el.ask(text);
  return true;
}

/** Flameco AI bottom-right on every signed-in page of a kit app. Renders nothing visible until Alpha allows it. */
export function DougWidget({ config }: { config: DougConfig }) {
  const ref = useRef<AlphaChatEl | null>(null);
  const { api, app, appName, widgetBase = DEFAULT_WIDGET_BASE, getToken, suggestions, currentPath, navigate } = config;

  // Load the widget once (it loads Flameco AI by itself).
  useEffect(() => {
    if (document.querySelector('script[data-alpha-widget]')) return;
    const s = document.createElement('script');
    s.src = `${widgetBase.replace(/\/?$/, '/')}alpha-chat.js`;
    s.async = true;
    s.dataset.alphaWidget = '1';
    document.head.appendChild(s);
  }, [widgetBase]);

  // Fresh token before every request; Flameco AI's links to this app open without a reload.
  useEffect(() => {
    const onNavigate = (e: Event) => {
      if (!navigate) return;
      e.preventDefault();
      navigate((e as CustomEvent<{ path: string }>).detail.path);
    };
    let el: AlphaChatEl | null = null;
    void customElements.whenDefined('alpha-chat').then(() => {
      if (!ref.current) return;
      el = ref.current;
      el.getToken = () => getToken();
      if (suggestions) el.suggestions = suggestions;
      el.addEventListener('alpha-navigate', onNavigate);
    });
    return () => el?.removeEventListener('alpha-navigate', onNavigate);
    // getToken/suggestions are read once; the token itself is read per request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // The page on screen, updated on every navigation.
  useEffect(() => {
    void customElements.whenDefined('alpha-chat').then(() => {
      if (!ref.current) return;
      const path = (currentPath ?? window.location.pathname).replace(/\/+$/, '') || '/';
      ref.current.context = { page: { path, title: document.title.slice(0, 200) } };
    });
  }, [currentPath]);

  // createElement, not JSX: World and Legacy ERP declare <alpha-chat> in JSX themselves.
  return React.createElement('alpha-chat', { ref, api, app, 'app-name': appName });
}
